import { useState, type ChangeEvent } from 'react'
import { useSignupProgressContext } from '@/context/SignupProgressContext'
import { Button, Flex, Input, LabelForm, useToastPopup } from '@collable/ui'
import { usePostVerification, useCheckVerification } from '@/api/auth'
import type { GoogleSignupRequest } from '@/api/auth/type'

interface PersonalAuthenticationContentProps {
  setPersonalAuthenticationSuccess: (success: boolean) => void
  personalAuthenticationSuccess: boolean
  errorInputs: string[]
  setErrorInputs: (inputs: string[]) => void
}

const PersonalAuthenticationContent = ({
  setPersonalAuthenticationSuccess,
  personalAuthenticationSuccess,
  errorInputs,
  setErrorInputs,
}: PersonalAuthenticationContentProps) => {
  const { mutate: postVerification } = usePostVerification()
  const { mutate: checkVerification } = useCheckVerification()
  const { signupInfo, onChangeSignupInfo } = useSignupProgressContext()
  const [certificationCodePosted, setCertificationCodePosted] = useState(false)

  const { addToastPopup } = useToastPopup()

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChangeSignupInfo(e.target.name as keyof GoogleSignupRequest, e.target.value)
    if (errorInputs.includes(e.target.name))
      setErrorInputs(errorInputs.filter(input => input !== e.target.name))
  }

  const personalAuthenticationBtnVisible = signupInfo?.name && signupInfo?.phoneNumber.length === 11

  const onClickCertificationPost = () => {
    postVerification(
      {
        type: 'SMS',
        target: signupInfo?.phoneNumber ?? '',
      },
      {
        onSuccess: res => {
          setCertificationCodePosted(true)
          onChangeSignupInfo('verificationKey', res.verificationKey)
          addToastPopup({
            type: 'normal',
            status: 'positive',
            message: '휴대전화 번호로 문자를 발송했습니다',
          })
        },
      },
    )
  }

  const onClickCertificationCheck = () => {
    checkVerification(
      {
        verificationKey: signupInfo?.verificationKey ?? '',
        secret: signupInfo?.secret ?? '',
      },
      {
        onSuccess: () => {
          setPersonalAuthenticationSuccess(true)
          addToastPopup({
            type: 'normal',
            status: 'positive',
            message: '인증이 완료되었습니다',
          })
        },
        onError: () => {
          setErrorInputs(errorInputs.concat('secret'))
        },
      },
    )
  }

  return (
    <>
      <LabelForm
        label="이름"
        name="name"
        required
        value={signupInfo?.name ?? ''}
        size="large"
        onChange={onChange}
        width="100%"
        placeholder="이름 입력"
      />
      <Flex direction="column" gap={8}>
        <LabelForm
          label="휴대전화 번호"
          required
          name="phoneNumber"
          value={signupInfo?.phoneNumber ?? ''}
          size="large"
          onChange={onChange}
          isError={errorInputs.includes('phoneNumber')}
          width="100%"
          placeholder="- 없이 입력"
          type="tel"
          maxLength={11}
        />
        {certificationCodePosted && (
          <Input
            type="tel"
            maxLength={6}
            value={signupInfo?.secret ?? ''}
            onChange={onChange}
            name="secret"
            size="large"
            isError={errorInputs.includes('secret')}
            width="100%"
            placeholder="000000"
          />
        )}
      </Flex>
      {personalAuthenticationBtnVisible && (
        <Button
          kind="solid"
          color="primary"
          onClick={certificationCodePosted ? onClickCertificationCheck : onClickCertificationPost}
          size="large"
          width="100%"
          type="button"
          disabled={personalAuthenticationSuccess}
        >
          {certificationCodePosted ? '인증번호 확인' : '인증번호 전송'}
        </Button>
      )}
      {certificationCodePosted && !personalAuthenticationSuccess && (
        <Button
          kind="soft"
          color="primary"
          onClick={onClickCertificationPost}
          size="large"
          width="100%"
          type="button"
        >
          인증번호 재발급
        </Button>
      )}
    </>
  )
}

export default PersonalAuthenticationContent
